import React, {useEffect, useState} from "react";

import {
	BillLine,
	BillRestControllerApiFactory,
	Budget,
	BudgetChangeDto,
	BudgetPayment,
	BudgetPaymentRestControllerApiFactory,
	BudgetRestControllerApiFactory,
} from "arteco-api-client-ts";
import {FormContent} from "../../../../components/form/form_inputs/form_buttons";
import {FormInput} from "../../../../components/form/form_inputs/form_input";
import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogContent,
	DialogTitle,
	Grid,
	Link as MuiLink,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableFooter,
	TableHead,
	TableRow,
	Typography
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import {useForm, UseFormProps} from "../../../../components/form/form_model";
import {useApiManager} from "../../../../components/utils/api";
import FormAutocomplete from "../../../../components/form/form_inputs/form_autocomplete";
import {types} from "../../../../components/form/types";
import {useCustomerNotes} from "../bill/form";
import FormToggle from "../../../../components/form/form_inputs/form_toggle";
import FormSelect from "../../../../components/form/form_inputs/form_select";
import {numberToString} from "../../../../components/utils/numbers";
import {Link, useNavigate} from "react-router-dom";
import {btnSx, EmptyState, euros, Page, PageHeader, Panel, SoftChip, tableContainerSx, tableSx} from "../../../../components/kit";
import {dateToString} from "../../../../components/utils/dates";
import {BUDGET_KIND_LABELS} from "../bill/labels";
import {RecordAttachments} from "../../../../components/record_attachments";

export const BudgetEditPage = () => {
	const navigate = useNavigate();
	const apiManager = useApiManager();
	const [showLines, setShowLines] = useState<boolean>(false);
	const [lines, setLines] = useState<BillLine[]>([]);
	const [payments, setPayments] = useState<BudgetPayment[]>([]);
	const [changes, setChanges] = useState<BudgetChangeDto[]>([]);

	const ctrl = apiManager.factory(BudgetRestControllerApiFactory);
	const billCtrl = apiManager.factory(BillRestControllerApiFactory);
	const paymentCtrl = apiManager.factory(BudgetPaymentRestControllerApiFactory);
	const {customerNotes, setCustomerId} = useCustomerNotes();


	const loadPayments = (budgetId: number) => {
		paymentCtrl.pageBudgetPayment(0, 100, undefined, budgetId, undefined, undefined, undefined, undefined, false)
		.then(resp => setPayments(resp.data?.content || []));
	};

	const form = useForm({
		fetcher: (id) => {
			const res = ctrl.getBudget(id)
			res.then(d => setCustomerId(d.data.customer.id))
			return res;
		},
		updater: (bean) => ctrl.saveBudget(bean),
		remover: (id) => ctrl.deleteOrDismissBudget(id),
		factory: () => {
			return {} as Budget;
		},
		postLoad: (bean) => {
			if (bean.id) loadPayments(bean.id);
			// Historial de cambios (#13746): quién tocó qué y cuándo.
			if (bean.id) ctrl.changesBudget(bean.id).then(resp => setChanges(resp.data || []));
		},
		extraActions: [
			{
				label: "Líneas de facturas",
				action: () => {
					setShowLines(true);
				},
			},
		]
	} as UseFormProps<Budget>);

	useEffect(() => {
		if (showLines) {
			billCtrl.budgetLinesBill(form.getId() as number)
			.then(resp => setLines(resp.data))
		}
	}, [showLines]);


	const averagePriceHour = (lines: BillLine[]) => {
		let avg = 0;
		let quantity = 0;
		if (lines.length > 0) {
			for (let line of lines) {
				const avg1 = line.quantity * line.unitNetPrice;
				avg += avg1;
				quantity += line.quantity;
			}

		}
		if (quantity > 0) {
			return numberToString(avg / quantity);
		}
		return "";
	}

	// Cuota normalizada a mes y a año, para que un total escrito como cuota salte a la vista: el
	// 28944 de GEIB entró como 80.000 €/mes (#13744).
	const MESES: { [k: string]: number } = {MENSUAL: 1, TRIMESTRAL: 3, ANUAL: 12};
	const esCuota = form.value("kind") === "CUOTA";
	const cuotaMensual = (form.value("periodAmount") || 0) / (MESES[form.value("billingPeriod") || "MENSUAL"] || 1);

	// Aceptado sin versión ni precio pactado (#14411): avisa, no bloquea. Un acuerdo marco lleva precio sin
	// versión y un importe cerrado, versión sin precio; con uno basta.
	const sinPrecioPactado = !!form.value("acceptedDate") && !esCuota
		&& !String(form.value("tariffVersion") || "").trim() && (form.value("agreedHourRate") ?? "") === "";

	// Entrega (#13749): aviso cuando faltan 10 días o menos para la fecha comprometida y no se
	// ha entregado. La conformidad y el cierre real alimentan el KPI de la lista.
	const esProyecto = form.value("kind") === "PROYECTO";
	const diasParaEntrega = form.value("committedDeliveryDate") && !form.value("deliveredDate")
		? Math.ceil((new Date(form.value("committedDeliveryDate")).getTime() - Date.now()) / 86400000)
		: null;

	const totalQuantity = (lines: BillLine[]) => {

		let quantity = 0;
		if (lines.length > 0) {
			for (let line of lines) {
				quantity += line.quantity;
			}

		}
		return numberToString(quantity);
	}

	const id = form.getId();
	const amount = form.value("amountWithoutTaxes") || 0;
	const hours = form.value("totalHours") || 0;
	const customerName = form.value("customer")?.shortName || form.value("customer")?.name;
	const kind = form.value("kind");

	return (
		<Page>
			<PageHeader back="/app/admin/billing/budget"
						title={id ? (form.value("title") || "Presupuesto") : "Nuevo presupuesto"}
						subtitle={id ? (
							<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
								<span>{[customerName, "presupuesto " + id].filter(Boolean).join(" · ")}</span>
								{kind && <SoftChip label={BUDGET_KIND_LABELS[kind] || kind} tone="info"/>}
								{form.value("completed") && <SoftChip label="Completado" tone="neutral"/>}
							</Stack>
						) : undefined}/>

			<FormContent form={form} withoutPaper>

				<Grid container spacing={3}>
					<Grid item xs={12} md={8}>
						<Stack spacing={3}>
							<Panel title="Presupuesto">
								<Grid container spacing={2}>
									<Grid item xs={12} sm={6}>
										<FormAutocomplete field={"customer"} label={"Cliente"} type={types.customers}
														  onChange={(t, c) => setCustomerId(c?.id)}/>
									</Grid>
									<Grid item xs={12} sm={6}>
										<FormInput field={"title"} label={"Título"}/>
									</Grid>
									<Grid item xs={12} sm={6}>
										<FormAutocomplete field={"organizationUnit"} label={"Unidad"}
														  type={types.organizationUnits}/>
									</Grid>
									<Grid item xs={12} sm={6}>
										<FormAutocomplete field={"owner"} label={"Asignado"} type={types.people}/>
									</Grid>
									<Grid item xs={12} sm={4}>
										<FormSelect field={"kind"} label={"Tipo"} type={types.budgetKind}
													helperText={"Proyecto, bolsa, T&M o cuota (Care, iguala, hosting)"}/>
									</Grid>
								</Grid>
							</Panel>

							<Panel title="Fechas" subtitle="El intervalo de fechas se utiliza para dividir las horas por el período.">
								<Grid container spacing={2}>
									<Grid item xs={12} sm={4}>
										<FormInput type={"date"} field={"startDate"} label={"Inicio"}/>
									</Grid>
									<Grid item xs={12} sm={4}>
										<FormInput type={"date"} field={"endDate"} label={"Fin"}
												   helperText={"El intervalo de fechas se utilizará para distribuir las horas"}/>
									</Grid>
									<Grid item xs={12} sm={4}>
										<FormInput type={"date"} field={"guaranteeEndDate"} label={"Fin de garantía"}/>
									</Grid>
								</Grid>
								<FormToggle field={"prorated"}
											label={"Prorratear entre fechas de inicio y fin"}/>
							</Panel>

							{esCuota && (
								<Panel title="Cuota">
									<Grid container spacing={2}>
										<Grid item xs={12} sm={4}>
											<FormSelect field={"billingPeriod"} label={"Periodicidad"} type={types.billingPeriod}/>
										</Grid>
										<Grid item xs={12} sm={4}>
											<FormInput field={"periodAmount"} type={"number"} label={"Importe por periodo sin IVA"}
													   helperText={"Lo que se factura cada periodo"}/>
										</Grid>
										<Grid item xs={12} sm={4}>
											<FormInput field={"periodHours"} type={"number"} label={"Horas incluidas por periodo"}/>
										</Grid>
										<Grid item xs={12} sm={4}>
											<FormInput field={"supportProject"} label={"Proyecto de soporte (Redmine)"}
													   helperText={"Donde el cliente abre sus solicitudes, p. ej. coresa-soporte"}/>
										</Grid>
										<Grid item xs={12} sm={4}>
											<FormInput field={"responseHours"} type={"number"} label={"Plazo de respuesta (h laborables)"}
													   helperText={"Care S 18 · M 9 · L 4. Vacío: sin compromiso"}/>
										</Grid>
										<Grid item xs={12} sm={4}>
											<FormInput field={"criticalResponseHours"} type={"number"} label={"Plazo para lo crítico (h laborables)"}
													   helperText={"Prioridad Urgente o Inmediata. Vacío: el mismo"}/>
										</Grid>
										<Grid item xs={12} sm={4}>
											<FormInput field={"renewalDate"} type={"date"} label={"Próxima renovación"}
													   helperText={"Se renueva sola si nadie avisa (marco tarifario §6)"}/>
										</Grid>
										<Grid item xs={12} sm={4}>
											<FormInput field={"noticeDeadline"} type={"date"} label={"Fin del preaviso"}
													   helperText={"Vacío: un mes antes de la renovación"}/>
										</Grid>
									</Grid>
									<Alert sx={{mt: 1, borderRadius: 2}} severity={cuotaMensual > 5000 ? "warning" : "info"}>
										{numberToString(cuotaMensual)} €/mes · {numberToString(cuotaMensual * 12)} €/año.
										{cuotaMensual > 5000 && " ¿Seguro que es una cuota? Si es el total de un contrato, el tipo es proyecto o bolsa."}
									</Alert>
								</Panel>
							)}

							<Panel title="Importe y horas" subtitle="Con horas o importe a 0 se considera T&M.">
								<Grid container spacing={2} alignItems="center">
									<Grid item xs={12} sm={4}>
										<FormInput field={"totalHours"} type={"number"} label={"Total horas"}
												   helperText={"Si es 0 se considera T&M"}/>
									</Grid>
									<Grid item xs={12} sm={4}>
										<FormInput field={"amountWithoutTaxes"} type={"number"} label={"Importe sin IVA"}
												   helperText={"Si es 0 se considera T&M"}/>
									</Grid>
									<Grid item xs={12} sm={4}>
										<Box sx={{px: 2, py: 1.25, borderRadius: 2, bgcolor: "grey.50", border: "1px solid", borderColor: "divider", mb: 2}}>
											<Typography variant="caption" color="text.secondary" display="block">Precio por hora</Typography>
											<Typography variant="h6" fontWeight={700} sx={{fontVariantNumeric: "tabular-nums"}}>
												{hours > 0 ? euros(amount / hours) : "—"}
											</Typography>
										</Box>
									</Grid>
									<Grid item xs={12} sm={4}>
										<FormInput field={"tariffVersion"} label={"Versión de tarifa"}
												   helperText={"Con la que se vendió, p. ej. 2026.8"}/>
									</Grid>
									<Grid item xs={12} sm={4}>
										<FormInput field={"agreedHourRate"} type={"number"} label={"Precio por hora pactado sin IVA"}
												   helperText={"Si se rellena, las horas de este presupuesto se facturan a este precio"}/>
									</Grid>
								</Grid>
								<FormToggle field={"completed"}
											label={"Completado (ocultar)"}/>
								<FormToggle field={"directDebit"}
											label={"Domiciliado (sus facturas se cobran por adeudo SEPA)"}/>
								{form.value("directDebit") && (
									<Alert sx={{mt: 1, borderRadius: 2}} severity={"warning"}>
										Si el cliente no tiene mandato SEPA activo, la factura NO saldrá
										domiciliada: se emitirá por transferencia y se avisará por correo.
										Así no se da por cobrado un recibo que el banco no puede cobrar.
									</Alert>
								)}
							</Panel>

							<Panel title="Aceptación y entrega">
								<Grid container spacing={2}>
									<Grid item xs={12} sm={4}>
										<FormInput type={"date"} field={"acceptedDate"} label={"Fecha de aceptación"}
												   helperText={"Fecha en que el cliente aceptó el presupuesto"}/>
									</Grid>
									{esProyecto && (
										<>
											<Grid item xs={12} sm={4}>
												<FormInput type={"date"} field={"committedDeliveryDate"} label={"Entrega comprometida"}
														   helperText={"Obligatoria si está aceptado. El KPI mide contra la primera"}/>
											</Grid>
											<Grid item xs={12} sm={4}>
												<FormInput type={"date"} field={"deliveredDate"} label={"Entregado el"}/>
											</Grid>
										</>
									)}
								</Grid>
								{sinPrecioPactado && (
									<Alert sx={{mb: 2, borderRadius: 2}} severity={"warning"}>
										Aceptado sin versión de tarifa ni precio por hora pactado. Anótalos en «Importe y horas» con
										los de la propuesta firmada: sin precio pactado, sus horas se facturan al precio de contrato
										del cliente o a la tarifa de quien imputa.
									</Alert>
								)}
								{esProyecto && diasParaEntrega != null && diasParaEntrega <= 10 && (
									<Alert sx={{mb: 2, borderRadius: 2}} severity={diasParaEntrega < 0 ? "error" : "warning"}>
										{diasParaEntrega < 0
											? `La entrega comprometida venció hace ${-diasParaEntrega} días y no consta entregado.`
											: `Faltan ${diasParaEntrega} días para la entrega comprometida.`}
									</Alert>
								)}
								{esProyecto && form.value("deliveredDate") && (
									<Grid container spacing={2}>
										<Grid item xs={12} sm={6}>
											<FormSelect field={"deliveryAcceptance"} label={"Conformidad de la entrega"}
														type={types.deliveryAcceptance}
														helperText={"Tácita solo con las condiciones que la prevén"}/>
										</Grid>
										<Grid item xs={12} sm={6}>
											<FormInput type={"date"} field={"deliveryAcceptedDate"} label={"Fecha de la conformidad"}/>
										</Grid>
										<Grid item xs={12} sm={6}>
											<FormInput field={"deliveryAcceptedBy"} label={"Quién la da"}/>
										</Grid>
										<Grid item xs={12} sm={6}>
											<FormInput field={"deliveryAcceptanceRef"} label={"Dónde está (enlace)"}/>
										</Grid>
									</Grid>
								)}
								<FormInput field={"analysisLocation"}
										   label={"Ubicación del análisis"}
										   helperText={"Indicar la ubicación del documento con el análisis"}
										   rows={1}/>
								<FormInput field={"customerAcceptance"}
										   label={"Ubicación de la aceptación del cliente"}
										   helperText={"Debe estar archivado en Drive o en el correo. Indicar cómo localizarlo"}
										   rows={2}/>
							</Panel>

							<Panel title="Textos de factura y notas">
								<FormInput field={"invoiceDescription"} label={"Descripción de factura"}
										   helperText={"Concepto principal que aparecerá en la cabecera de la factura"}
										   rows={2}/>
								<FormInput field={"invoiceLineDescription"} label={"Descripción de línea de factura"}
										   helperText={"Descripción de la línea de detalle de la factura"}
										   rows={2}/>
								<FormInput field={"description"} label={"Descripción"} rows={2}/>
								<FormInput field={"observation"} label={"Observación (privada)"} rows={5}/>
							</Panel>

							{id && (
								<Panel title="Pagos" subtitle="Hitos de facturación previstos. Pulsa uno para editarlo."
									   action={<Button size={"small"} variant={"outlined"} startIcon={<AddRoundedIcon/>} sx={btnSx}
													   onClick={() => navigate(`/app/admin/billing/budget-payment/new?budgetId=${id}`)}>
										   Añadir pago
									   </Button>}>
									{payments.length === 0 ? (
										<EmptyState title="Sin pagos registrados"/>
									) : (
										<TableContainer sx={{...tableContainerSx, overflowX: "auto"}}>
											<Table size={"small"} sx={{...tableSx, minWidth: 520}}>
												<TableHead>
													<TableRow>
														<TableCell>Descripción de factura</TableCell>
														<TableCell align={"right"}>Importe neto</TableCell>
														<TableCell>Fecha prevista</TableCell>
														<TableCell>Estado</TableCell>
													</TableRow>
												</TableHead>
												<TableBody>
													{payments.map(p => (
														<TableRow
															key={p.id}
															hover
															sx={{cursor: "pointer"}}
															onClick={() => navigate(`/app/admin/billing/budget-payment/${p.id}`)}>
															<TableCell>{p.description}</TableCell>
															<TableCell align={"right"} sx={{fontVariantNumeric: "tabular-nums"}}>
																{p.amount != null ? euros(Number(p.amount)) : "—"}
															</TableCell>
															<TableCell>{p.plannedDate ? dateToString(p.plannedDate) : "—"}</TableCell>
															<TableCell>
																{p.invoiced
																	? <SoftChip label="Facturado" tone="success"/>
																	: <SoftChip label="Pendiente" tone="neutral"/>}
															</TableCell>
														</TableRow>
													))}
												</TableBody>
											</Table>
										</TableContainer>
									)}
								</Panel>
							)}

							{id && changes.length > 0 && (
								<Panel title="Cambios" subtitle="Quién tocó qué y cuándo.">
									<TableContainer sx={{...tableContainerSx, overflowX: "auto"}}>
										<Table size={"small"} sx={{...tableSx, minWidth: 560}}>
											<TableHead>
												<TableRow>
													<TableCell>Fecha</TableCell>
													<TableCell>Usuario</TableCell>
													<TableCell>Tipo</TableCell>
													<TableCell>Qué cambió</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{[...changes].reverse().map(c => (
													<TableRow key={c.revision}>
														<TableCell sx={{whiteSpace: "nowrap"}}>{(c.date || "").replace("T", " ").substring(0, 16)}</TableCell>
														<TableCell>{c.username}</TableCell>
														<TableCell>{c.type}</TableCell>
														<TableCell>
															{(c.fields || []).map(f => (
																<div key={f.name}>
																	<b>{f.name}</b>: {f.before ?? "—"} → {f.after ?? "—"}
																</div>
															))}
														</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</TableContainer>
								</Panel>
							)}
						</Stack>
					</Grid>

					<Grid item xs={12} md={4}>
						<Stack spacing={3} sx={{position: {md: "sticky"}, top: {md: 16}}}>
							{id && (
								<Panel title="Resumen">
									<Stack spacing={1}>
										<Stack direction="row" justifyContent="space-between">
											<Typography variant="body2" color="text.secondary">Importe sin IVA</Typography>
											<Typography variant="body2" fontWeight={700} sx={{fontVariantNumeric: "tabular-nums"}}>{euros(amount)}</Typography>
										</Stack>
										<Stack direction="row" justifyContent="space-between">
											<Typography variant="body2" color="text.secondary">Horas</Typography>
											<Typography variant="body2" fontWeight={700} sx={{fontVariantNumeric: "tabular-nums"}}>
												{hours ? hours.toLocaleString("es-ES", {maximumFractionDigits: 1}) : "T&M"}
											</Typography>
										</Stack>
										<Stack direction="row" justifyContent="space-between">
											<Typography variant="body2" color="text.secondary">Pagos facturados</Typography>
											<Typography variant="body2" fontWeight={700}>
												{payments.filter(p => p.invoiced).length} de {payments.length}
											</Typography>
										</Stack>
									</Stack>
									<Button fullWidth variant="outlined" sx={{...btnSx, mt: 2}} onClick={() => setShowLines(true)}>
										Ver líneas facturadas
									</Button>
								</Panel>
							)}
							<Panel title="Notas del cliente">
								<Typography variant="body2" color={customerNotes ? "text.primary" : "text.disabled"}
											sx={{whiteSpace: "pre-wrap", fontSize: 13}}>
									{customerNotes || "Sin notas."}
								</Typography>
							</Panel>
						</Stack>
					</Grid>
				</Grid>
			</FormContent>

			<Box sx={{mt: 3}}>
				<RecordAttachments recordType="BUDGET" recordId={id}/>
			</Box>

			<Dialog open={showLines} onClose={() => setShowLines(false)} fullWidth maxWidth="md"
					PaperProps={{sx: {borderRadius: 3}}}>
				<DialogTitle sx={{fontWeight: 700}}>Líneas facturadas</DialogTitle>
				<DialogContent>
					<TableContainer sx={{...tableContainerSx, overflowX: "auto"}}>
						<Table size="small" sx={{...tableSx, minWidth: 600}}>
							<TableHead>
								<TableRow>
									<TableCell>Factura</TableCell>
									<TableCell>Fecha</TableCell>
									<TableCell width={"35%"}>Concepto</TableCell>
									<TableCell align={"right"}>Unidades</TableCell>
									<TableCell align={"right"}>Importe unitario</TableCell>
									<TableCell align={"right"}>IVA</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{lines.map(l => (
									<TableRow key={"line-" + l.id}>
										<TableCell>
											<MuiLink component={Link} to={"/app/admin/billing/bill/" + l.bill.id} underline="hover" fontWeight={600}>
												{l.bill?.id}
											</MuiLink>
										</TableCell>
										<TableCell>{l.bill?.issueDate ? dateToString(l.bill.issueDate) : ""}</TableCell>
										<TableCell>{l.concept}</TableCell>
										<TableCell align={"right"} sx={{fontVariantNumeric: "tabular-nums"}}>{numberToString(l.quantity)}</TableCell>
										<TableCell align={"right"} sx={{fontVariantNumeric: "tabular-nums"}}>{numberToString(l.unitNetPrice)}</TableCell>
										<TableCell align={"right"} sx={{fontVariantNumeric: "tabular-nums"}}>{l.taxType}</TableCell>
									</TableRow>
								))}
								{lines.length == 0 && (
									<TableRow>
										<TableCell colSpan={6} sx={{textAlign: "center", color: "text.secondary", py: 3}}>No hay líneas facturadas</TableCell>
									</TableRow>
								)}
							</TableBody>
							<TableFooter sx={{"& td": {fontWeight: 700, color: "text.primary", fontSize: 14, bgcolor: "grey.50"}}}>
								<TableRow>
									<TableCell align={"right"} colSpan={3}>Facturadas</TableCell>
									<TableCell align={"right"}>{totalQuantity(lines)}</TableCell>
									<TableCell align={"right"}>{averagePriceHour(lines)}</TableCell>
									<TableCell/>
								</TableRow>
								{form.value("totalHours") > 0 && (
									<>
										<TableRow>
											<TableCell align={"right"} colSpan={3}>Totales</TableCell>
											<TableCell align={"right"}>{numberToString(form.value("totalHours"))}</TableCell>
											<TableCell/>
											<TableCell/>
										</TableRow>
										<TableRow>
											<TableCell align={"right"} colSpan={3}>Diferencia</TableCell>
											<TableCell align={"right"}>{numberToString(form.value("totalHours") - parseFloat(totalQuantity(lines)))}</TableCell>
											<TableCell/>
											<TableCell/>
										</TableRow>
									</>
								)}
							</TableFooter>
						</Table>
					</TableContainer>
				</DialogContent>
			</Dialog>
		</Page>
	);
};
